import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys, useAuthenticatedClient } from '@/api/queries'
import { POLL_ANALYSIS, POLL_NORMAL } from '@/api/polling'
import { manufacturerDataKeys } from './query-keys'
import type {
  LeadTimeCheckStatus,
  ManufacturerDataActionPage,
  ManufacturerDataActionsParams,
  ManufacturerDataAgent,
  ManufacturerDataChange,
} from './model'

/** One change's items sit under the data agent's keys, so a revert's invalidation refreshes them too. */
const changeItemsKey = (changeId: number) => [...manufacturerDataKeys.all, 'change-items', changeId] as const

/** The manufacturer's data@ conversations, in the PO conversation shape (USA-1474). */
export function useManufacturerDataThreads(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: manufacturerDataKeys.threads(manufacturerUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturers/{manufacturer_uuid}/data-threads', {
        params: { path: { manufacturer_uuid: manufacturerUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!manufacturerUuid,
    // While the agent reads an email, check back often so what it did lands without a reload.
    refetchInterval: (query) =>
      query.state.data?.some((thread) =>
        thread.analysis.some((a) => a.state === 'queued' || a.state === 'running'),
      )
        ? POLL_ANALYSIS
        : POLL_NORMAL,
  })
}

/** The weekly lead-time check, the agent's items and what it changed. */
export function useManufacturerDataAgent(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: manufacturerDataKeys.agent(manufacturerUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturers/{manufacturer_uuid}/data-agent', {
        params: { path: { manufacturer_uuid: manufacturerUuid } },
      })
      if (error) throw error
      return data
    },
    enabled: !!manufacturerUuid,
    refetchInterval: POLL_NORMAL,
  })
}

/**
 * One item by id, for an item no manufacturer page holds: a mailbox our mail system refuses (USA-1729).
 * It sits under the item lists' key, so resolving it refreshes it with them.
 */
export function useManufacturerDataAction(actionId: number) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...manufacturerDataKeys.actionsAll, 'item', actionId] as const,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturer-data-actions/{action_id}', {
        params: { path: { action_id: actionId } },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

/** Turn the weekly lead-time check on or off; the switch flips at once and rolls back on failure. */
export function useSetLeadTimeCheck(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const key = manufacturerDataKeys.agent(manufacturerUuid)
  return useMutation({
    mutationFn: async (enabled: boolean | null) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/manufacturers/{manufacturer_uuid}/lead-time-check', {
        params: { path: { manufacturer_uuid: manufacturerUuid } },
        body: { enabled },
      })
      if (error) throw error
      return data
    },
    onMutate: async (enabled) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<ManufacturerDataAgent>(key)
      queryClient.setQueryData<ManufacturerDataAgent>(key, (old) =>
        old
          ? {
              ...old,
              lead_time_check: {
                ...old.lead_time_check,
                explicit: enabled,
                enabled: enabled ?? old.lead_time_check.on_by_default,
              },
            }
          : old,
      )
      return { previous }
    },
    onSuccess: (status: LeadTimeCheckStatus) => {
      queryClient.setQueryData<ManufacturerDataAgent>(key, (old) =>
        old ? { ...old, lead_time_check: status } : old,
      )
    },
    onError: (_error, _enabled, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

/** Manufacturers with open data items, for the Data role on the Action Items page. */
export function useManufacturerDataActions(params: ManufacturerDataActionsParams, enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: manufacturerDataKeys.actions(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturer-data-actions', {
        params: { query: params },
      })
      if (error) throw error
      return data
    },
    enabled,
    refetchInterval: POLL_NORMAL,
  })
}

/**
 * Match an email from an unknown sender to its manufacturer. The row leaves the unmatched group at once
 * and the list refetches to show it under the manufacturer.
 */
export function useAssignDataActionManufacturer() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ actionId, manufacturerUuid }: { actionId: number; manufacturerUuid: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/manufacturer-data-actions/{action_id}/assign-manufacturer', {
        params: { path: { action_id: actionId } },
        body: { manufacturer_uuid: manufacturerUuid },
      })
      if (error) throw error
      return data
    },
    onMutate: async ({ actionId }) => {
      await queryClient.cancelQueries({ queryKey: manufacturerDataKeys.actionsAll })
      const previous = queryClient.getQueriesData<ManufacturerDataActionPage>({
        queryKey: manufacturerDataKeys.actionsAll,
      })
      queryClient.setQueriesData<ManufacturerDataActionPage>({ queryKey: manufacturerDataKeys.actionsAll }, (old) =>
        old
          ? {
              ...old,
              items: old.items
                .map((group) =>
                  group.manufacturer ? group : { ...group, actions: group.actions.filter((a) => a.id !== actionId) },
                )
                .filter((group) => group.manufacturer || group.actions.length > 0),
            }
          : old,
      )
      return { previous }
    },
    onError: (_error, _vars, context) => {
      for (const [key, data] of context?.previous ?? []) queryClient.setQueryData(key, data)
    },
    onSettled: (_data, _error, { manufacturerUuid }) => {
      queryClient.invalidateQueries({ queryKey: manufacturerDataKeys.actionsAll })
      queryClient.invalidateQueries({ queryKey: manufacturerDataKeys.agent(manufacturerUuid) })
      queryClient.invalidateQueries({ queryKey: manufacturerDataKeys.threads(manufacturerUuid) })
    },
  })
}

/** Each product's, discount's or special's before and after within one change, loaded when it is opened (USA-796). */
export function useManufacturerDataChangeItems(changeId: number, enabled: boolean) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: changeItemsKey(changeId),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/manufacturer-data-changes/{change_id}/items', {
        params: { path: { change_id: changeId } },
      })
      if (error) throw error
      return data
    },
    enabled,
  })
}

/**
 * Put back what one row of "What the agent changed" replaced (one change, or one email's same lead time on
 * several models). The row reads Reverted at once and rolls back on failure; returns each change as saved.
 */
export function useRevertManufacturerDataChange(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const key = manufacturerDataKeys.agent(manufacturerUuid)
  return useMutation({
    mutationFn: async (changeIds: readonly number[]) => {
      const client = await getClient()
      const saved: ManufacturerDataChange[] = []
      for (const changeId of changeIds) {
        const { data, error } = await client.POST('/dashboard/manufacturer-data-changes/{change_id}/revert', {
          params: { path: { change_id: changeId } },
        })
        if (error) throw error
        saved.push(data)
      }
      return saved
    },
    onMutate: async (changeIds) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<ManufacturerDataAgent>(key)
      const revertedAt = new Date().toISOString().replace('Z', '')
      queryClient.setQueryData<ManufacturerDataAgent>(key, (old) =>
        old
          ? {
              ...old,
              changes: old.changes.map((change) =>
                changeIds.includes(change.id)
                  ? { ...change, status: 'reverted', reverted_at: revertedAt, reverted_by: null }
                  : change,
              ),
            }
          : old,
      )
      return { previous }
    },
    onSuccess: (saved) => {
      queryClient.setQueryData<ManufacturerDataAgent>(key, (old) =>
        old
          ? { ...old, changes: old.changes.map((change) => saved.find((next) => next.id === change.id) ?? change) }
          : old,
      )
    },
    onError: (_error, _changeIds, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: manufacturerDataKeys.all })
      // The put-back discounts and prices show on the manufacturer's other tabs.
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.detail(manufacturerUuid) })
    },
  })
}
